/**
 * Native Bridge communication with Ring iOS app
 */

/** Credentials returned by Ring app's native bridge */
interface NativeBridgeCredentials {
    /** JWT access token for authentication */
    accessToken: string;
    /** Device hardware identifier */
    hardwareId: string;
}

/** Message sent from web to Ring app */
interface NativeBridgeMessage {
    /** Unique message ID for request/response correlation */
    id: number;
    /** Operation name (e.g., 'get-credentials', 'refresh-credentials') */
    operation: string;
    /** Optional message payload */
    body?: any;
}

/** Response received from Ring app */
interface NativeBridgeResponse {
    /** Message ID matching the original request */
    id: number;
    /** Operation name from the original request */
    operation: string;
    /** Success ('ok') or failure ('error') indicator */
    result: 'ok' | 'error';
    /** Response data when result is 'ok' */
    body?: any;
    /** Error message when result is 'error' */
    error?: string;
}

/**
 * TypeScript declaration for the native bridge object injected by Ring iOS app.
 * This object only exists at runtime when the website is loaded in Ring app's WebViewController.
 * In regular browsers, window.ringapp will be undefined.
 */
declare global {
    interface Window {
        /** 
         * Native bridge object injected by Ring iOS app's WebViewController.
         * Provides communication channel between web content and native app.
         */
        ringapp?: {
            /** Send message to Ring app */
            sendMessage: (data: string) => void;
            /** Receive message from Ring app (overridden by our handler) */
            onMessage: (data: string) => void;
        };
    }
}

class NativeBridge {
    private messageId = 1;
    private pendingRequests = new Map<number, { resolve: Function; reject: Function }>();

    constructor() {
        if (!this.isAvailable()) {
            console.log(`Native bridge not available`);
            return;
        }

        // Override the onMessage handler to process responses
        window.ringapp!.onMessage = (data: string) => {

            // Handle our response
            try {
                const response: NativeBridgeResponse = JSON.parse(data);
                this.handleResponse(response);
            } catch (error) {
                console.error('Failed to parse response:', error);
            }
        };

        console.log(`Bridge setup complete`);
    }

    isAvailable(): boolean {
        return typeof window.ringapp === 'object' &&
            typeof window.ringapp?.sendMessage === 'function';
    }

    private handleResponse(response: NativeBridgeResponse) {
        const pending = this.pendingRequests.get(response.id);
        if (pending) {
            this.pendingRequests.delete(response.id);

            if (response.result === 'ok') {
                pending.resolve(response.body);
            } else {
                pending.reject(new Error(response.error || 'Unknown error'));
            }
        } else {
            console.log(`No pending request found for ID ${response.id}`);
        }
    }

    private sendRequest<T>(operation: string, body?: any): Promise<T> {
        return new Promise((resolve, reject) => {
            if (!this.isAvailable()) {
                reject(new Error('Native bridge not available'));
                return;
            }

            const id = this.messageId++;
            const message: NativeBridgeMessage = { id, operation, body };

            this.pendingRequests.set(id, { resolve, reject });

            try {
                window.ringapp!.sendMessage(JSON.stringify(message));
            } catch (error) {
                this.pendingRequests.delete(id);
                reject(error);
            }
        });
    }

    async getCredentials(): Promise<NativeBridgeCredentials> {
        return this.sendRequest<NativeBridgeCredentials>('get-credentials');
    }

    async refreshCredentials(): Promise<NativeBridgeCredentials> {
        return this.sendRequest<NativeBridgeCredentials>('refresh-credentials');
    }
}

export const nativeBridge = new NativeBridge();